import React from 'react';
import { Button, Grid, Typography, Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { InfoOutlined } from '@mui/icons-material';

/**
 * SVG-based circular score ring.
 * Takes a percentage (0-100) and renders a donut chart.
 */
const ScoreRing = ({ percentage, size = 80 }) => {
    const theme = useTheme();
    const strokeWidth = 8;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    const getColor = (pct) => {
        if (pct >= 80) return theme.palette.success.main;
        if (pct >= 50) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    return (
        <Box sx={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
            <svg width={size} height={size}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={theme.palette.divider}
                    strokeWidth={strokeWidth}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={getColor(percentage)}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <Box sx={{
                position: 'absolute',
                top: 0, left: 0, bottom: 0, right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Typography style={{ fontSize: size <= 50 ? '10px' : size <= 70 ? '13px' : '16px', fontWeight: 700 }}>
                    {percentage}%
                </Typography>
            </Box>
        </Box>
    );
};

const READINESS_TOOLTIP = "Product Readiness analyzes your product data and assigns a score based on consistency, completeness and quality. It highlights issues and suggests improvements to help your products perform better across sales channels and AI agents.";

const ReadinessInfoIcon = () => (
    <Tooltip title={READINESS_TOOLTIP} arrow>
        <InfoOutlined sx={{ fontSize: 18, ml: 0.5, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }} />
    </Tooltip>
);

/**
 * Product Readiness score card.
 * Placed above Variations on the right column of product details.
 *
 * Props:
 * - score: number|null — the current score (0-1 from API, displayed as percentage)
 * - suggestions: string[] — feedback items from API
 * - onScoreProduct: () => void — called when user clicks "Score Product" or "Re-Score"
 * - isLoading: boolean
 */
interface ProductScoreCardProps {
    score?: number | null;
    suggestions?: Array<string | { Issue?: string; Recommendation?: string }>;
    onScoreProduct: () => void;
    onViewDetails: () => void;
    isLoading?: boolean;
    compact?: boolean;
}

function ProductScoreCard({ score, suggestions, onScoreProduct, onViewDetails, isLoading, compact }: ProductScoreCardProps) {
    const hasScore = score !== null && score !== undefined;
    const hasSuggestions = suggestions && suggestions.length > 0;
    const percentage = hasScore ? Math.round(score * 10) : 0;
    const previewItems = suggestions ? suggestions.slice(0, 3) : [];

    return (
        <Grid container direction="column" style={{ width: '100%' }}>
            {hasScore ? (
                compact ? (
                    // No bullet points — two columns: title+buttons left, ring right
                    <Grid container style={{ width: '100%' }}>
                        <Grid item style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography style={{ fontSize: '20px', fontWeight: 700 }}>
                                Product Readiness <ReadinessInfoIcon />
                            </Typography>
                            <Grid item style={{ display: 'flex', gap: '8px' }}>
                                {hasSuggestions && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        onClick={onViewDetails}
                                    >
                                        View Details
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={onScoreProduct}
                                    disabled={isLoading}
                                >
                                    Re-Score
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                            <ScoreRing percentage={percentage} />
                        </Grid>
                    </Grid>
                ) : (
                    // Has bullet points — title + ring + bullets + buttons
                    <Grid container direction="column" style={{ width: '100%' }}>
                        <Typography style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', textAlign: 'left' }}>
                            Product Readiness <ReadinessInfoIcon />
                        </Typography>
                    <Grid container justifyContent="space-between" alignItems="flex-end" style={{ width: '100%' }}>
                        <Grid item style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <ScoreRing percentage={percentage} />
                            <Grid item>
                                {previewItems.map((item, index) => {
                                    const text = typeof item === 'object' && item !== null
                                        ? (item.Issue || item.Recommendation || '')
                                        : String(item);
                                    return (
                                        <Typography
                                            key={index}
                                            variant="body2"
                                            color="textSecondary"
                                            style={{ textAlign: 'left', marginBottom: '4px' }}
                                        >
                                            &bull; {text}
                                        </Typography>
                                    );
                                })}
                            </Grid>
                        </Grid>
                        <Grid item style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            {hasSuggestions && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={onViewDetails}
                                >
                                    View Details
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={onScoreProduct}
                                disabled={isLoading}
                            >
                                Re-Score
                            </Button>
                        </Grid>
                    </Grid>
                    </Grid>
                )
            ) : (
                <Grid container style={{ width: '100%' }}>
                    <Grid item style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography style={{ fontSize: '20px', fontWeight: 700 }}>
                            Product Readiness <ReadinessInfoIcon />
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ margin: '8px 0', textAlign: 'left' }}>
                            Score this product to evaluate its readiness.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onScoreProduct}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Scoring...' : 'Score Product'}
                        </Button>
                    </Grid>
                    <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                        <ScoreRing percentage={0} />
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
}

export { ScoreRing, ReadinessInfoIcon };
export default ProductScoreCard;
